/**
 * 화면이 지금 띄우고 있는 것 — 화면이 보고한 대로, **한 장의 그림으로**.
 *
 * 보고 방식이 둘이고 이 컴포넌트가 그 차이를 흡수한다.
 *
 *   - `frame` — 화면을 찍은 그림. 받는 즉시 붙들어 건다(`stills.ts`).
 *   - `video` — 그림이 아니라 **재생 위치**. 같은 영상에서 그 위치의 장면을 그림으로
 *     떠내 건다.
 *
 * 영상을 이렇게 다루는 이유는 비용이다. 영상 한 장을 찍어 보내면 수십 KB 지만
 * 재생 위치는 몇 바이트고, 콘솔은 같은 영상을 이미 받을 수 있다.
 *
 * **어디서도 영상을 틀지 않는다.** 화면 상세도 모니터링 벽도 들어온 순간 한 번 집은
 * 보고를 그림으로 보여준다(`screenCapture.ts`). 예전에는 상세가 보고된 위치를 따라
 * 영상을 실제로 틀었는데, 따라 틀려면 5초마다 새 보고가 필요하고 그 보고를 위해
 * 화면들이 계속 자기 화면을 찍는다.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Video } from 'lucide-react';
import type { ScreenReport } from '../api/client.js';
import { frameImage, holdStill, MISSING, videoFrame, type Still } from './stills.js';

/** 이 보고를 무엇으로 그릴지. */
export type ScreenView = 'frame' | 'still' | 'status';

/** 첫 장면으로 쓰는 위치(초). */
export const FIRST_SCENE_SEC = 0.1;

/**
 * 보고 하나를 어떻게 그릴지 정한다.
 *
 * 분기가 컴포넌트 안에 흩어져 있으면 "영상은 틀지 않는다"는 약속을 시험으로 지킬 수
 * 없다. 판단만 따로 떼어 둔다.
 *
 * - `frame` 은 이미 그림이다.
 * - `video` 는 같은 영상의 한 장면(`still`)이다. 재생으로 가는 길이 없다.
 * - 그릴 주소를 모르면 그릴 것이 없다 — 그래도 상태는 말한다(`status`).
 */
export function decideScreenView(
  report: Pick<ScreenReport, 'kind' | 'url'>,
  videoUrl: string | null,
): ScreenView {
  if (report.kind === 'frame') return report.url ? 'frame' : 'status';
  if (report.kind !== 'video') return 'status';
  return videoUrl ? 'still' : 'status';
}

/**
 * 영상의 어느 장면을 걸지(초) — 화면이 보고한 위치.
 *
 * 첫 장면보다 앞으로는 가지 않는다. 막 시작한 영상이 0초로 보고되면 첫 장면을 건다.
 */
export function stillSeconds(report: Pick<ScreenReport, 'position_sec'>): number {
  return Math.max(FIRST_SCENE_SEC, report.position_sec ?? 0);
}

/** 보고 하나에 그림 하나다. 같은 보고를 크게 보기로 열거나 다시 그려도 다시 받지 않는다. */
function stillKey(report: ScreenReport): string {
  return `${report.asset_id ?? '-'}@${report.taken_at}`;
}

export function LiveScreen({
  report,
  /** 이 화면이 틀고 있던 영상의 주소. `kind === 'video'` 일 때만 쓴다. */
  videoUrl,
  /**
   * 찍어 올린 그림이 서버에서 이미 지워졌다 — 화면이 그새 새 그림을 올렸다.
   * 부르는 쪽이 이 보고를 버리고 새로 찍는다.
   */
  onMissing,
}: {
  report: ScreenReport;
  videoUrl: string | null;
  onMissing?: (report: ScreenReport) => void;
}): ReactNode {
  const view = decideScreenView(report, videoUrl);

  if (view === 'frame' && report.url) {
    /*
     * `object-contain` 이다. 화면 비율이 16:9 가 아닐 수 있고 잘라서 채우면 그 사실이
     * 감춰진다 — 세로 화면에 가로 콘텐츠가 나가고 있는 것이 여기서 보여야 한다.
     */
    return (
      <HeldStill
        frameKey={stillKey(report)}
        url={report.url}
        load={frameImage}
        onMissing={onMissing ? () => onMissing(report) : undefined}
      />
    );
  }

  if (view === 'still' && videoUrl) {
    return <VideoStill frameKey={stillKey(report)} url={videoUrl} atSec={stillSeconds(report)} />;
  }

  if (report.kind === 'video') {
    // 영상 주소를 모르면 그릴 것이 없다. 그래도 화면이 무엇을 하고 있는지는 말한다.
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-slate-900 text-[12px] text-white/70">
        <Video className="h-6 w-6" strokeWidth={1.6} />
        <span>{playbackLabel(report)}</span>
      </div>
    );
  }

  return null;
}

/** 화면이 무엇을 하고 있는지 한 줄로. 벽과 상세가 같은 문구를 쓴다. */
function playbackLabel(report: ScreenReport): string {
  if (report.paused) return '화면에서 멈춰 있음';
  return `영상 재생 중 · ${(report.position_sec ?? 0).toFixed(0)}초 지점`;
}

/** 영상의 한 장면 — 그림으로 떠내 건다. */
export function VideoStill({
  frameKey,
  url,
  atSec,
}: {
  /** 같은 값이면 같은 그림이다. */
  frameKey: string;
  url: string;
  atSec: number;
}): ReactNode {
  return (
    <HeldStill
      frameKey={frameKey}
      url={url}
      atSec={atSec}
      load={(key, source) => videoFrame(key, source, atSec)}
    />
  );
}

/**
 * 붙든 그림을 건다.
 *
 * 붙드는 동안은 검은 상자다. 붙들지 못하면 원래 주소로 대신한다 — 그림은 `<img>`,
 * 영상은 그 장면에 멈춘 `<video>`(틀지 않고, 눌러지지도 않는다).
 */
function HeldStill({
  frameKey,
  url,
  load,
  atSec,
  onMissing,
}: {
  frameKey: string;
  url: string;
  load: (key: string, url: string) => Promise<Still>;
  /** 영상이면 그 장면의 위치. 없으면 찍어 올린 그림이다. */
  atSec?: number;
  onMissing?: () => void;
}): ReactNode {
  /*
   * 주소는 이 그림을 처음 그릴 때의 것을 붙든다. 서명 URL 은 조회마다 문자열이 달라져서,
   * 그걸 따라가면 같은 그림을 다시 받는다.
   */
  const pinned = useRef({ key: frameKey, url });
  if (pinned.current.key !== frameKey) pinned.current = { key: frameKey, url };
  const loadRef = useRef(load);
  loadRef.current = load;
  const missingRef = useRef(onMissing);
  missingRef.current = onMissing;

  const [held, setHeld] = useState<{ key: string; still: Still } | null>(null);

  useEffect(() => {
    let alive = true;
    // 거는 동안은 붙든 것을 놓지 말라고 알린다 — 넘쳐서 놓이면 이 `<img>` 가 깨진다(`stills.ts`).
    const release = holdStill(frameKey);
    void loadRef.current(frameKey, pinned.current.url).then((still) => {
      if (!alive) return;
      setHeld({ key: frameKey, still });
      if (still === MISSING) missingRef.current?.();
    });
    return () => {
      alive = false;
      release();
    };
  }, [frameKey]);

  if (held?.key !== frameKey || held.still === MISSING) return <div className="h-full w-full bg-black" />;

  if (held.still !== null) {
    return <img src={held.still} alt="" className="h-full w-full bg-black object-contain" decoding="async" />;
  }

  if (atSec === undefined) {
    return <img src={pinned.current.url} alt="" className="h-full w-full bg-black object-contain" decoding="async" />;
  }

  return (
    <video
      src={`${pinned.current.url}#t=${atSec}`}
      muted
      playsInline
      preload="metadata"
      controls={false}
      className="pointer-events-none h-full w-full bg-black object-contain"
    />
  );
}
